import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Button, Dialog, DialogActions } from '@mui/material';
import classNames from 'clsx';
import { ReactComponent as WarningIcon } from '@/img/icons/warning.svg';
import { ReactComponent as ChevronIcon } from '@/img/icons/chevron-down.svg';
import './index.scss';

const Reservation = ({ label, variant, hotelName, checkInDate, checkOutDate, shortId }) => {
    const { t } = useTranslation();

    return (
        <div className="double-booking-dialog__reservation">
            <div className={classNames('double-booking-dialog__reservation-label', `double-booking-dialog__reservation-label--${variant}`)}>
                {label}
            </div>
            <div className="double-booking-dialog__reservation-hotel-name">{hotelName}</div>

            <div className="double-booking-dialog__reservation-date-wrapper">
                <div className="double-booking-dialog__reservation-date-title">{t('DoubleBookingDetected.CheckIn')}:</div>
                <div className="double-booking-dialog__reservation-date">{checkInDate}</div>
            </div>

            <div className="double-booking-dialog__reservation-date-wrapper">
                <div className="double-booking-dialog__reservation-date-title">{t('DoubleBookingDetected.CheckOut')}:</div>
                <div className="double-booking-dialog__reservation-date">{checkOutDate}</div>
            </div>

            {shortId && (
                <div className="double-booking-dialog__reservation-id">
                    {t('DoubleBookingDetected.ReservationId')}: {shortId}
                </div>
            )}
        </div>
    );
};

const DoubleBookingDialog = ({
    isOpen,
    responseAction,
    newReservationDetails = {},
    existingReservationsDetails = [],
}) => {
    const { t } = useTranslation();

    const safeExistingReservations = existingReservationsDetails || [];
    const firstExistingReservationDetails = safeExistingReservations[0] || {};
    const additionalExistingReservationsDetails = safeExistingReservations.slice(1);

    const [isOpenSimilar, setIsOpenSimilar] = useState(false);

    return (
        <Dialog className="double-booking-dialog" open={isOpen} onClose={() => responseAction(false)}>
            <div className="double-booking-dialog__warning-icon-wrapper">
                <div className="double-booking-dialog__warning-icon">
                    <WarningIcon />
                </div>
            </div>

            <div className="double-booking-dialog__title">{t('DoubleBookingDetected.Title')}</div>
            <div className="double-booking-dialog__subtitle">{t('DoubleBookingDetected.SubTitle')}</div>

            <div className="double-booking-dialog__reservations-wrapper">
                <Reservation
                    label={t('DoubleBookingDetected.NewReservationDetails')}
                    variant="new"
                    hotelName={newReservationDetails.hotelName}
                    checkInDate={newReservationDetails.checkInDate}
                    checkOutDate={newReservationDetails.checkOutDate}
                />

                <div className="double-booking-dialog__reservation-separate" />

                <Reservation
                    label={t('DoubleBookingDetected.ExistingReservationDetails')}
                    variant="existing"
                    hotelName={firstExistingReservationDetails.hotelName}
                    checkInDate={firstExistingReservationDetails.checkInDate}
                    checkOutDate={firstExistingReservationDetails.checkOutDate}
                    shortId={firstExistingReservationDetails.shortId}
                />
            </div>

            {additionalExistingReservationsDetails.length > 0 && (
                <>
                    <div
                        className={classNames('double-booking-dialog__similar-reservation-text', {
                            'double-booking-dialog__similar-reservation-text--open': isOpenSimilar,
                        })}
                        onClick={() => setIsOpenSimilar((prev) => !prev)}
                    >
                        <Trans
                            i18nKey="DoubleBookingDetected.Similar"
                            values={{ count: additionalExistingReservationsDetails.length }}
                        />{' '}
                        <ChevronIcon />
                    </div>

                    {isOpenSimilar && additionalExistingReservationsDetails.map((additionalReservationDetail) => (
                        <div className="double-booking-dialog__reservations-wrapper" key={additionalReservationDetail.shortId}>
                            <Reservation
                                label={t('DoubleBookingDetected.ExistingReservationDetails')}
                                variant="existing"
                                hotelName={additionalReservationDetail.hotelName}
                                checkInDate={additionalReservationDetail.checkInDate}
                                checkOutDate={additionalReservationDetail.checkOutDate}
                                shortId={additionalReservationDetail.shortId}
                            />
                        </div>
                    ))}
                </>
            )}

            <DialogActions className="double-booking-dialog__actions">
                <Button
                    variant="outlined"
                    size="large"
                    onClick={() => responseAction(false)}
                    className="double-booking-dialog__action-button"
                >
                    {t('DoubleBookingDetected.Cancel')}
                </Button>
                <Button
                    variant="contained"
                    size="large"
                    onClick={() => responseAction(true)}
                    className="double-booking-dialog__action-button"
                >
                    {t('DoubleBookingDetected.Book')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DoubleBookingDialog;
